import React, { useEffect, useState, useRef } from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

interface CelebrationOverlayProps {
  show: boolean;
  onComplete: () => void;
}

export const CelebrationOverlay: React.FC<CelebrationOverlayProps> = ({ show, onComplete }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const dotLottieRef = useRef<any>(null);

  useEffect(() => {
    if (show) {
      console.log('[CelebrationOverlay] Showing celebration!');
      setIsVisible(true);
      setIsFadingOut(false);

      // After 2 seconds, start fading out
      const fadeTimer = setTimeout(() => {
        console.log('[CelebrationOverlay] Starting fade out');
        setIsFadingOut(true);
      }, 2000);

      // After 2.5 seconds total (2s display + 0.5s fade), hide completely
      const hideTimer = setTimeout(() => {
        console.log('[CelebrationOverlay] Hiding celebration');
        setIsVisible(false);
        setIsFadingOut(false);
        onComplete();
      }, 2500);

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [show, onComplete]);

  // Set up the dotLottie ref callback to stop looping after first play
  useEffect(() => {
    if (dotLottieRef.current && isVisible) {
      const dotLottie = dotLottieRef.current;

      const handleComplete = () => {
        console.log('[CelebrationOverlay] Animation completed once');
        // Animation will stay on last frame since loop is false
      };

      dotLottie.addEventListener('complete', handleComplete);

      return () => {
        dotLottie.removeEventListener('complete', handleComplete);
      };
    }
  }, [isVisible]);

  console.log('[CelebrationOverlay] Render - isVisible:', isVisible, 'show:', show, 'fadingOut:', isFadingOut);

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(2px)',
        animation: isFadingOut ? 'fadeOut 0.5s ease-out' : 'fadeIn 0.3s ease-in-out',
        opacity: isFadingOut ? 0 : 1
      }}
    >
      <div className="flex flex-col items-center justify-center">
        <div className="w-64 h-64 md:w-80 md:h-80">
          <DotLottieReact
            dotLottieRefCallback={(ref) => {
              dotLottieRef.current = ref;
            }}
            src="/materials/Trophy.lottie"
            loop={false}
            autoplay
          />
        </div>
        <h2
          className="text-4xl md:text-6xl font-black mt-4 tracking-wider text-foreground"
          style={{
            animation: 'bounceIn 0.6s ease-out',
          }}
        >
          MISE SPLNĚNA!
        </h2>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }
        
        @keyframes bounceIn {
          0% {
            opacity: 0;
            transform: scale(0.3) translateY(20px);
          }
          50% {
            opacity: 1;
            transform: scale(1.15);
          }
          70% {
            transform: scale(0.95);
          }
          100% {
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};
